<template>
    <div v-loading="loading" class="user-portfolio-events">
        <div class="user-portfolio-events__head">
            <div class="user-portfolio-events__head-title">
                <span>Участие в мероприятиях</span>
                <strong v-if="events.length" @click="popupOpened = true">
                    {{ events.length }}
                </strong>
            </div>
            <div
                v-if="events.length > limit"
                class="user-portfolio-events__head-see-all"
                @click="popupOpened = true"
            >
                Смотреть все
            </div>
        </div>
        <div v-if="events.length" class="user-portfolio-events__list">
            <div
                v-for="(item, index) in eventsLimited"
                :key="index"
                class="user-portfolio-events__list-item"
            >
                <div class="user-portfolio-events__list-item-name">
                    {{ item.name }}
                </div>
                <div class="user-portfolio-events__list-item-date">
                    {{ eventDateString(item.dateStart, item.dateEnd) }}
                </div>
            </div>
        </div>
        <div v-else class="user-portfolio-events__empty">
            Пользователь еще не участвовал в мероприятиях
        </div>


        <Modal :opened="popupOpened" @close="popupOpened = false">
            <div v-loading="loading" class="user-portfolio-events user-portfolio-events--modal">
                <div class="user-portfolio-events__head">
                    <div class="user-portfolio-events__head-title">
                        <span>Участие в мероприятиях:</span>
                        <strong v-if="events.length">
                            {{ events.length }}
                        </strong>
                    </div>
                </div>
                <div class="user-portfolio-events__list">
                    <div
                        v-for="(item, index) in events"
                        :key="index"
                        class="user-portfolio-events__list-item"
                    >
                        <div class="user-portfolio-events__list-item-name">
                            {{ item.name }}
                        </div>
                        <div class="user-portfolio-events__list-item-date">
                            {{ eventDateString(item.dateStart, item.dateEnd) }}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import moment from "moment/moment";
import Modal from "@/components/UI/Modal.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";

export default {
    components: {
        Modal,
    },

    data: () => ({
        loading: false,
        limit: 4,
        popupOpened: false,
        eventsSPK: [],
    }),

    async created() {
        this.loading = true;

        await Promise.all([
            this.loadRegistryItems({ type: 'spk_project_type' }),
            this.loadRegistryItems({ type: 'stage_history' }),
        ]);

        await this.initEvents(this.getUser.userId);

        this.loading = false;
    },

    watch: {
        async 'getUser.userId'(userId) {
            this.loading = true;

            await this.initEvents(userId);

            this.loading = false;
        },
    },

    computed: {
        ...mapGetters('data', [
            'getRegistryItems',
        ]),
        ...mapGetters('newUsers', [
            'getUser',
            'getUserProjects',
        ]),

        eventsLimited() {
            return (this.events).slice(0, this.limit);
        },

        events() {
            return this.getList();
        },
    },

    methods: {
        ...mapActions('data', [
            'loadRegistryItems',
        ]),
        ...mapActions('newUsers', [
            'loadUserProjects',
            'loadUserProjectsSPK',
        ]),
        ...mapMutations('newUsers', [
            'setUserEvents',
        ]),
        async initEvents(userId) {
            if (this.getRegistryItems('spk_project_type').length === 0) {
                await this.loadRegistryItems({ type: 'spk_project_type', withDeleted: true });
            }

            const projectSPKTypes = this.getRegistryItems('spk_project_type')
                .filter(v => ['event', 'events', 'facetoface_event'].indexOf(v.field_name) !== -1)
                .map(v => v.id)
            ;

            const data = await Promise.all([
                this.loadUserProjects(userId),
                this.loadUserProjectsSPK({
                    userId,
                    filters: {
                        types: projectSPKTypes,
                    },
                    fetchOnly: true,
                }),
            ]);

            this.eventsSPK = data[1] || [];
        },

        parseProjectDate(date) {
            if (!date) {
                return null;
            }

            const arr = date.toString().split(/^(\d{4})(\d{2})(\d{2})$/);

            return arr.length === 5 ? `${arr[1]}-${arr[2]}-${arr[3]}` : null;
        },

        eventDateString(dateStart, dateEnd) {
            return [
                dateStart ? moment(dateStart).format('DD.MM.YYYY') : null,
                dateEnd ? moment(dateEnd).format('DD.MM.YYYY') : null,
            ].filter(v => v).join(' – ');
        },

        getList() {
            const ret = [
                ...this.eventsSPK
                    .map(v => {
                        v._source = 'spk';
                        return v;
                    }),
                ...this.getUserProjects
                    .filter(
                        v => [
                            'event',
                            'events',
                        ].indexOf(v.project_type) !== -1
                    )
                    .map(v => {
                        v._source = 'projects';
                        v.dateStart = this.parseProjectDate(v.date_start);
                        v.dateEnd = this.parseProjectDate(v.date_end);
                        return v;
                    }),
            ].sort((a, b) => {
                if ((!a.dateStart || !b.dateStart) && !(!a.dateStart && !b.dateStart)) {
                    return a.dateStart ? -1 : 1;
                }

                const startDiff = moment(a.dateStart || new Date()).diff(b.dateStart || new Date());

                if (startDiff !== 0) {
                    return startDiff > 0 ? -1 : 1;
                }

                if (!a.dateEnd || !b.dateEnd) {
                    return !a.dateEnd && !b.dateEnd ? 0 : a.dateEnd ? -1 : 1;
                }

                const endDiff = moment(a.dateEnd || new Date()).diff(b.dateEnd || new Date());

                return endDiff === 0 ? 0 : endDiff > 0 ? -1 : 1;
            });
            this.setUserEvents(ret);
            return ret;
        },
    },
};

</script>

<style scoped lang="scss">
@import "~@/styles/rem";
@import '~@/styles/variables';

.user-portfolio-events {
    display: flex;
    flex-direction: column;
    gap: mrem(40);

    @include on-desktop {
        gap: rem(40);
    }

    &__head {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        gap: mrem(16);

        @include on-desktop {
            gap: rem(16);
            align-items: center;
            flex-direction: row;
        }

        &-title {
            font-weight: 700;
            font-size: mrem(18);
            line-height: mrem(24);

            @include on-desktop {
                font-size: rem(18);
                line-height: rem(24);
            }

            strong {
                color: $color-main-blue;
                font-weight: 500;
                cursor: pointer;

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        &-see-all {
            color: $color-main-blue;
            font-weight: 400;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }
    }
    &__list {
        display: flex;
        flex-direction: column;
        gap: mrem(20);

        @include on-desktop {
            gap: rem(20);
        }

        &-item {
            background: $color-main-notice-lighter;
            display: flex;
            flex-direction: column;
            padding: mrem(20) mrem(16);
            border-radius: mrem(16);
            gap: mrem(16);

            @include on-desktop {
                padding: rem(20) rem(16);
                border-radius: rem(16);
                gap: rem(16);
            }

            &-name {
                font-weight: 700;
            }
            &-date {
                color: $color-secondary-grey;
                font-weight: 500;
                font-size: mrem(14);
                line-height: mrem(22);

                @include on-desktop {
                    font-size: rem(14);
                    line-height: rem(22);
                }
            }
        }
    }

    &__empty {
        color: $color-main-primary;
        font-weight: 500;
    }

    &--modal {
        gap: mrem(24);
        font-family: $font-montserrat;
        font-style: normal;
        font-weight: 400;
        color: #2A2626;
        font-size: mrem(16);
        line-height: mrem(22);

        @include on-desktop {
            gap: rem(24);
            max-width: rem(650);
            font-size: rem(16);
            line-height: rem(22);
        }

        .user-portfolio-events__head {
            &-title {
                font-size: mrem(24);

                @include on-desktop {
                    font-size: rem(24);
                }

                strong {
                    font-weight: 400;
                    cursor: initial;

                    &:hover {
                        text-decoration: none;
                    }
                }
            }
        }
    }
}

</style>
